import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaUsers, FaUserMd, FaFileAlt, FaCog, FaChartLine, FaCalendarAlt, FaBell, FaSearch } from 'react-icons/fa';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const PageContainer = styled.div`
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const DashboardHeader = styled.div`
  background: linear-gradient(120deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.accent} 100%);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: white;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
    border-radius: 50%;
    transform: translate(30%, -30%);
  }
`;

const Title = styled.h1`
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
  font-weight: 700;
  position: relative;
  z-index: 1;
  
  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
  font-size: 1rem;
  position: relative;
  z-index: 1;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  padding: 0.5rem 1rem;
  width: 100%;
  max-width: 400px;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(5px);
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
  
  input {
    background: transparent;
    border: none;
    color: white;
    padding: 0.5rem;
    flex: 1;
    outline: none;
    font-size: 0.95rem;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.7);
    }
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2.5rem;
  }
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: all 0.3s ease;
  border-top: 4px solid ${({ $color, theme }) => $color || theme.colors.primary};
  
  @media (min-width: 768px) {
    padding: 2rem 1.5rem;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatIcon = styled.div`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: ${({ $color, theme }) => $color || theme.colors.primary};
  background: ${({ $bgColor }) => $bgColor || 'rgba(41, 23, 89, 0.1)'};
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  @media (min-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.2rem;
    width: 60px;
    height: 60px;
  }
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  font-size: 0.9rem;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  margin-top: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 2.5rem;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ViewAllLink = styled(Link)`
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ModulesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
`;

const ModuleCard = styled(Card)`
  height: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    
    button {
      background-color: ${({ theme }) => theme.colors.primary};
      color: white;
    }
  }
`;

const ModuleHeader = styled(Card.Header)`
  padding: 1.25rem;
  font-size: 1.1rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.background};
  
  @media (min-width: 768px) {
    padding: 1.5rem;
    font-size: 1.2rem;
  }
`;

const ModuleContent = styled(Card.Content)`
  padding: 1.25rem;
  flex: 1;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const ModuleFooter = styled(Card.Footer)`
  padding: 1.25rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const AdminDashboard = () => {
  return (
    <PageContainer>
      <DashboardHeader>
        <Title>Admin Dashboard</Title>
        <Subtitle>Welcome back! Here's an overview of the platform metrics and management tools.</Subtitle>
        
        <SearchBar>
          <FaSearch color="rgba(255, 255, 255, 0.7)" />
          <input type="text" placeholder="Search for users, content, or settings..." />
        </SearchBar>
      </DashboardHeader>
      
      <StatsGrid>
        <StatCard $color={({ theme }) => theme.colors.primary}>
          <StatIcon $bgColor="rgba(41, 23, 89, 0.1)">
            <FaUsers color="#291759" />
          </StatIcon>
          <StatValue>1,245</StatValue>
          <StatLabel>Total Users</StatLabel>
        </StatCard>
        
        <StatCard $color={({ theme }) => theme.colors.secondary}>
          <StatIcon $bgColor="rgba(33, 150, 243, 0.1)">
            <FaUserMd color="#2196F3" />
          </StatIcon>
          <StatValue>48</StatValue>
          <StatLabel>Healthcare Partners</StatLabel>
        </StatCard>
        
        <StatCard $color={({ theme }) => theme.colors.accent}>
          <StatIcon $bgColor="rgba(108, 99, 255, 0.1)">
            <FaFileAlt color="#6C63FF" />
          </StatIcon>
          <StatValue>156</StatValue>
          <StatLabel>Content Items</StatLabel>
        </StatCard>
        
        <StatCard $color={({ theme }) => theme.colors.warning}>
          <StatIcon $bgColor="rgba(255, 152, 0, 0.1)">
            <FaCog color="#FF9800" />
          </StatIcon>
          <StatValue>3</StatValue>
          <StatLabel>System Alerts</StatLabel>
        </StatCard>
      </StatsGrid>
      
      <SectionHeader>
        <SectionTitle>Management Tools</SectionTitle>
        <ViewAllLink to="/admin">View All Tools</ViewAllLink>
      </SectionHeader>
      
      <ModulesGrid>
        <ModuleCard>
          <ModuleHeader>User Management</ModuleHeader>
          <ModuleContent>
            <p>View and manage users, permissions, and account status. Monitor user activity and engagement metrics.</p>
          </ModuleContent>
          <ModuleFooter>
            <Button as={Link} to="/admin/users" variant="outline" fullWidth>
              Manage Users
            </Button>
          </ModuleFooter>
        </ModuleCard>
        
        <ModuleCard>
          <ModuleHeader>Partner Management</ModuleHeader>
          <ModuleContent>
            <p>Review and manage healthcare partner accounts, applications, and performance metrics. Set commission rates and view analytics.</p>
          </ModuleContent>
          <ModuleFooter>
            <Button as={Link} to="/admin/partners" variant="outline" fullWidth>
              Manage Partners
            </Button>
          </ModuleFooter>
        </ModuleCard>
        
        <ModuleCard>
          <ModuleHeader>Content Management</ModuleHeader>
          <ModuleContent>
            <p>Manage platform content, articles, and resources. Schedule content publication and track engagement metrics.</p>
          </ModuleContent>
          <ModuleFooter>
            <Button as={Link} to="/admin/content" variant="outline" fullWidth>
              Manage Content
            </Button>
          </ModuleFooter>
        </ModuleCard>
        
        <ModuleCard>
          <ModuleHeader>Wonder Klinik Management</ModuleHeader>
          <ModuleContent>
            <p>Add, edit, and manage nutritionists displayed in the "Hitta din Wonder Klinik" section for users.</p>
          </ModuleContent>
          <ModuleFooter>
            <Button as={Link} to="/admin/nutritionists" variant="outline" fullWidth>
              Manage Wonder Kliniker
            </Button>
          </ModuleFooter>
        </ModuleCard>
        
        <ModuleCard>
          <ModuleHeader>Platform Settings</ModuleHeader>
          <ModuleContent>
            <p>Configure system settings, integrations, preferences, and access controls. Manage site-wide announcements.</p>
          </ModuleContent>
          <ModuleFooter>
            <Button as={Link} to="/admin/settings" variant="outline" fullWidth>
              Manage Settings
            </Button>
          </ModuleFooter>
        </ModuleCard>
      </ModulesGrid>
      
      <SectionHeader>
        <SectionTitle>Performance Analytics</SectionTitle>
        <ViewAllLink to="/admin/analytics">View Detailed Reports</ViewAllLink>
      </SectionHeader>
      
      <Card style={{ borderRadius: '1rem', overflow: 'hidden', marginBottom: '2rem' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Platform Growth</h3>
          <FaChartLine size="1.5rem" style={{ color: '#291759' }} />
        </div>
        <div style={{ padding: '1.5rem', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>Growth chart placeholder - Implement with a chart library for actual metrics</p>
        </div>
      </Card>
    </PageContainer>
  );
};

export default AdminDashboard; 